/**
 * Shared Firestore CRUD for workflow projects.
 *
 * Generic helpers for the `workflows/{workflowSlug}/projects/{wid}` pattern
 * used by RFP and RFQ (default database).
 *
 * Supplier Evaluation uses a different path (`supplier-evaluation/{eid}`)
 * in the `sensei-workflows` database — see supplier-evaluation.ts.
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import { getFirebaseFirestore } from "@/platform/firebase/client";
import type { AuditEntry } from "@/modes/agentic-workflows/shared";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function db() { return getFirebaseFirestore(); }

function projectCol(workflowSlug: string) {
  return collection(db(), "workflows", workflowSlug, "projects");
}

function projectDoc(workflowSlug: string, wid: string) {
  return doc(db(), "workflows", workflowSlug, "projects", wid);
}

function subCol(workflowSlug: string, wid: string, sub: string) {
  return collection(db(), "workflows", workflowSlug, "projects", wid, sub);
}

function subDoc(workflowSlug: string, wid: string, sub: string, id: string) {
  return doc(db(), "workflows", workflowSlug, "projects", wid, sub, id);
}

/** Strip undefined values from an object so Firestore's updateDoc doesn't choke. */
export function removeUndefined<T extends Record<string, unknown>>(obj: T): T {
  const cleaned = {} as T;
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) (cleaned as Record<string, unknown>)[k] = v;
  }
  return cleaned;
}

// ─── Core CRUD ────────────────────────────────────────────────────────────────

export async function createWorkflowProject(
  workflowSlug: string,
  wid: string,
  data: Record<string, unknown>,
): Promise<void> {
  const ref = projectDoc(workflowSlug, wid);
  await setDoc(ref, { ...removeUndefined(data), id: wid, updated_at: new Date().toISOString() });
}

export async function getWorkflowProject<T = Record<string, unknown>>(
  workflowSlug: string,
  wid: string,
): Promise<T | null> {
  const ref = projectDoc(workflowSlug, wid);
  const snap = await getDoc(ref);
  return snap.exists() ? (snap.data() as T) : null;
}

export async function getUserWorkflowProjects<T = Record<string, unknown>>(
  workflowSlug: string,
  userId: string,
): Promise<T[]> {
  const ref = projectCol(workflowSlug);
  const q = query(ref, where("created_by", "==", userId), orderBy("updated_at", "desc"), limit(50));
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as T);
}

export async function getUserWorkflowProjectsWhereStakeholder<T = Record<string, unknown>>(
  workflowSlug: string,
  email: string,
): Promise<T[]> {
  const ref = projectCol(workflowSlug);
  const snap = await getDocs(ref);

  const results: T[] = [];
  snap.docs.forEach((d) => {
    const data = d.data();
    const stakeholders = data.step_04_stakeholders as Record<string, string[]> | undefined;
    if (
      stakeholders?.evaluator_emails?.includes(email) ||
      stakeholders?.approver_emails?.includes(email) ||
      stakeholders?.observer_emails?.includes(email) ||
      stakeholders?.supplier_emails?.includes(email)
    ) {
      results.push(data as T);
    }
  });
  return results;
}

export async function updateWorkflowData(
  workflowSlug: string,
  wid: string,
  data: Record<string, unknown>,
): Promise<void> {
  const ref = projectDoc(workflowSlug, wid);
  await updateDoc(ref, { ...removeUndefined(data), updated_at: new Date().toISOString() });
}

export async function deleteWorkflowProject(
  workflowSlug: string,
  wid: string,
): Promise<void> {
  const ref = projectDoc(workflowSlug, wid);
  await deleteDoc(ref);
}

// ─── State machine operations ─────────────────────────────────────────────────

export async function transitionWorkflowState(
  workflowSlug: string,
  wid: string,
  newState: string,
): Promise<void> {
  await updateWorkflowData(workflowSlug, wid, { workflow_state: newState });
  await logAuditEntry(workflowSlug, wid, {
    project_id: wid,
    action: "state_transition",
    performed_by: "system",
    performed_at: new Date().toISOString(),
    to_state: newState,
  });
}

export async function lockPhase(
  workflowSlug: string,
  wid: string,
  phase: number,
  userId: string,
): Promise<void> {
  await updateWorkflowData(workflowSlug, wid, {
    [`phase_locks.phase_${phase}_locked`]: true,
    "phase_locks.locked_at": new Date().toISOString(),
    "phase_locks.locked_by": userId,
  });
  await logAuditEntry(workflowSlug, wid, {
    project_id: wid,
    action: `phase_${phase}_locked`,
    performed_by: userId,
    performed_at: new Date().toISOString(),
  });
}

export async function cancelWorkflowProject(
  workflowSlug: string,
  wid: string,
  userId: string,
  reason?: string,
): Promise<void> {
  await transitionWorkflowState(workflowSlug, wid, "CANCELLED");
  await logAuditEntry(workflowSlug, wid, {
    project_id: wid,
    action: "project_cancelled",
    performed_by: userId,
    performed_at: new Date().toISOString(),
    details: reason ? { reason } : undefined,
  });
}

// ─── Subcollection operations ─────────────────────────────────────────────────

export async function logAuditEntry(
  workflowSlug: string,
  wid: string,
  entry: AuditEntry,
): Promise<void> {
  const auditId = entry.id ?? `audit-${Date.now()}`;
  const ref = subDoc(workflowSlug, wid, "audit_log", auditId);
  await setDoc(ref, {
    ...entry,
    id: auditId,
    performed_at: entry.performed_at ?? new Date().toISOString(),
  });
}

export async function getAuditLog(
  workflowSlug: string,
  wid: string,
): Promise<AuditEntry[]> {
  const ref = subCol(workflowSlug, wid, "audit_log");
  const q = query(ref, orderBy("performed_at", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as AuditEntry);
}

// ─── Supplier token auth ──────────────────────────────────────────────────────

export async function validateSupplierToken(
  workflowSlug: string,
  wid: string,
  token: string,
): Promise<boolean> {
  const project = await getWorkflowProject<Record<string, unknown>>(workflowSlug, wid);
  if (!project) return false;

  const stakeholders = project.step_04_stakeholders as Record<string, unknown> | undefined;
  const suppliers = stakeholders?.suppliers as Array<{ token?: string }> | undefined;
  if (!suppliers) return false;

  return suppliers.some((s) => s.token === token);
}